<script setup>
// The scroller is the table's own, so a wide column set never makes the page
// itself scroll sideways.
//
// `#cols` is optional and takes `<col>` elements. Passing it switches the table
// to `table-layout: fixed`, which is the only way to stop a column resizing
// when the row content changes — a header that swaps to a `colspan` cell
// otherwise takes every column width with it. Tables that don't pass it are
// untouched and stay on auto layout.
</script>

<template>
  <div class="xl-table__scroll">
    <table class="xl-table" :class="{ 'xl-table--fixed': !!$slots.cols }">
      <colgroup v-if="$slots.cols"><slot name="cols" /></colgroup>
      <thead v-if="$slots.head">
        <tr><slot name="head" /></tr>
      </thead>
      <tbody><slot /></tbody>
    </table>
  </div>
</template>

<style scoped>
.xl-table__scroll, .xl-table__scroll * { box-sizing: border-box; }

.xl-table__scroll { position: relative; overflow-x: auto; }

.xl-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--xl-font-family);
  font-size: var(--xl-type-body-medium-size);
}

.xl-table--fixed { table-layout: fixed; }

.xl-table :deep(th) {
  padding: 10px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  font-size: var(--xl-type-label-small-size);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  /* Text, so a text token — the border colour was unreadable at this size. */
  color: var(--xl-color-secondary-text);
  text-align: left;
  white-space: nowrap;
}

/* A sortable header wraps its label in a button, and the UA stylesheet sets
   `text-transform: none` on form controls — so those headers came out in
   sentence case while the plain ones beside them were uppercase. */
.xl-table :deep(th button) {
  text-transform: inherit;
  letter-spacing: inherit;
}

.xl-table :deep(td) {
  padding: 13px 16px;
  border-bottom: 1px solid var(--xl-color-border);
  color: var(--xl-color-primary-text);
  vertical-align: middle;
}

.xl-table :deep(tbody tr:last-child td) { border-bottom: 0; }

.xl-table :deep(tbody tr) { transition: background 0.12s; }

.xl-table :deep(tbody tr.is-clickable) { cursor: pointer; }

.xl-table :deep(tbody tr.is-clickable:hover),
.xl-table :deep(tbody tr.is-selected) { background: var(--xl-color-primary-container); }

/* The selection tint is the same token a brand tag fills with, so on a
   selected row the pills would dissolve into the background. */
.xl-table :deep(tbody tr.is-selected .xl-tag--brand) { background: #fff; }
</style>
